import React, { useEffect, useState } from "react";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CommonPopup from "..";
import QrCodeReader from "../../QrCodeReader";
import NFCReader from "../../NFCReader";

type Props = {
  popup: string;
  title?: string;
  onClose?: () => void;
  onResult?: (result?: string) => void;
};

function SpaceScannerPopup({
  popup,
  onClose,
  title: originTitle,
  onResult,
}: Props) {
  const [title, setTitle] = useState(originTitle);
  const [mode, setMode] = useState("");
  const loadingComponent = useFullScreenLoading({ isLoading: mode === "qr" });

  const extractJSON = (text: string): any => {
    const match = text.match(/{[^}]*}/);
    if (match) {
      const jsonString = match[0];
      try {
        return jsonString;
      } catch (error) {
        console.error("Invalid JSON string:", error);
        return "";
      }
    }
    console.error("No JSON object found in the text");
    return "";
  };

  const handleQrResult = (result: string, error?: any) => {
    if (!result || !mode) return;

    onResult?.(extractJSON(result));
    setMode("");
    onClose?.();
  };

  useEffect(() => {
    if ((originTitle?.length ?? 0) > 0) {
      setTitle(originTitle);
    }
  }, [originTitle]);

  return (
    <>
      {loadingComponent}
      <CommonPopup
        isOpen={popup === "spaceScanner"}
        title={title}
        unUseHorizonRule
        onClose={() => onClose?.()}
      >
        <div className="p-5 pt-0 flex justify-center gap-[60px] text-brand-neutral-80 text-sm leading-[18px]">
          <div
            className="flex flex-col gap-1.5 items-center"
            onClick={() => setMode("qr")}
          >
            <QrIcon />
            QR 인식
          </div>
          <div
            className="flex flex-col gap-1.5 items-center"
            onClick={() => setMode("nfc")}
          >
            <NFCIcon />
            NFC 코드 스캔
          </div>
        </div>
      </CommonPopup>

      {mode === "qr" && (
        <QrCodeReader onResult={handleQrResult} onClose={() => setMode("")} />
      )}
      {mode === "nfc" && (
        <NFCReader onResult={handleQrResult} onClose={() => setMode("")} />
      )}
    </>
  );
}

export const QrIcon = () => (
  <svg
    width="81"
    height="80"
    viewBox="0 0 81 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="1"
      y="0.5"
      width="79"
      height="79"
      rx="2.5"
      fill="white"
      stroke="white"
    />
    <rect
      x="25.6873"
      y="24.75"
      width="11.8613"
      height="11.8613"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <rect
      x="25.6873"
      y="43.3892"
      width="11.8613"
      height="11.8613"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <rect
      x="44.3263"
      y="43.3892"
      width="11.8613"
      height="11.8613"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M44.3263 36.6113H48.1958M56.1876 32.3751V36.6113H53.2223M56.1876 27.7153V24.75H53.2223M48.5625 24.75H44.3263V31.9302"
      stroke="url(#paint0_linear_1502_40815)"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M48.3754 28.8729V32.7393H51.5632"
      stroke="url(#paint1_linear_1502_40815)"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M52.6033 28.8542L52.6033 28.8721"
      stroke="url(#paint2_linear_1502_40815)"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M31.6171 30.596L31.6171 30.7646"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M31.6171 49.2351L31.6171 49.4038"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M50.2579 49.2351L50.2579 49.4038"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M65.9375 15L15.9375 15L15.9375 65L65.9375 65L65.9375 15Z"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1502_40815"
        x1="56.1876"
        y1="24.75"
        x2="43.5593"
        y2="28.0002"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_1502_40815"
        x1="48.3754"
        y1="32.7393"
        x2="51.8383"
        y2="32.0044"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_1502_40815"
        x1="52.6033"
        y1="28.8542"
        x2="52.6034"
        y2="28.8745"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
    </defs>
  </svg>
);

export const NFCIcon = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="79"
      height="79"
      rx="2.5"
      fill="white"
      stroke="white"
    />
    <path
      d="M64.468 29.1182C69.0201 35.6713 69.0351 44.3153 64.468 50.8813"
      stroke="url(#paint0_linear_1502_40823)"
      strokeWidth="3"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M60 33.4204C62.3408 37.5095 62.3408 42.5032 60 46.5795"
      stroke="url(#paint1_linear_1502_40823)"
      strokeWidth="3"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M15.532 50.8818C10.9799 44.3287 10.9649 35.6847 15.532 29.1187"
      stroke="url(#paint2_linear_1502_40823)"
      strokeWidth="3"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M20 46.5796C17.6592 42.4905 17.6592 37.4968 20 33.4205"
      stroke="url(#paint3_linear_1502_40823)"
      strokeWidth="3"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M60 55.5L60 65L20 65L20 55.5M20 24.625L20 15L60 15C60 19.8816 60 19.6184 60 24.5"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M34.3984 46H32.1328L27.5391 39.3594H27.4609V46H24.8047V34.6875H27.1016L31.6484 41.3281H31.7422V34.6875H34.3984V46ZM36.1172 34.6875H43.7891V36.8594H38.7734V39.25H43.2891V41.4219H38.7734V46H36.1172V34.6875ZM52.6797 38.7656C52.6068 38.1771 52.3698 37.7135 51.9688 37.375C51.5729 37.0365 51.0599 36.8698 50.4297 36.875C49.8776 36.875 49.3984 37.0104 48.9922 37.2812C48.5911 37.5521 48.2812 37.9479 48.0625 38.4688C47.849 38.9896 47.7422 39.6146 47.7422 40.3438C47.7422 41.0938 47.849 41.7292 48.0625 42.25C48.2812 42.7656 48.5911 43.1562 48.9922 43.4219C49.3932 43.6823 49.8672 43.8125 50.4141 43.8125C51.0234 43.8125 51.5286 43.6562 51.9297 43.3438C52.3307 43.0312 52.5807 42.599 52.6797 42.0469L55.3672 42.0625C55.2943 42.7604 55.0495 43.4219 54.6328 44.0469C54.2214 44.6719 53.651 45.1797 52.9219 45.5703C52.1927 45.9609 51.3411 46.1562 50.3672 46.1562C49.3359 46.1562 48.4167 45.9271 47.6094 45.4688C46.8021 45.0104 46.1693 44.3438 45.7109 43.4688C45.2526 42.5938 45.0234 41.5521 45.0234 40.3438C45.0234 39.1302 45.2552 38.0859 45.7188 37.2109C46.1823 36.3359 46.8177 35.6719 47.625 35.2188C48.4375 34.7604 49.3516 34.5312 50.3672 34.5312C51.2734 34.5312 52.0859 34.6979 52.8047 35.0312C53.5286 35.3646 54.112 35.8516 54.5547 36.4922C55.0026 37.1276 55.2734 37.8854 55.3672 38.7656H52.6797Z"
      fill="black"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1502_40823"
        x1="67.8877"
        y1="29.1182"
        x2="64.0121"
        y2="29.2749"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_1502_40823"
        x1="61.7556"
        y1="33.4204"
        x2="59.765"
        y2="33.4888"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_1502_40823"
        x1="12.1123"
        y1="50.8818"
        x2="15.9879"
        y2="50.7251"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_1502_40823"
        x1="18.2444"
        y1="46.5796"
        x2="20.235"
        y2="46.5112"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="1" stopColor="#E100A3" />
      </linearGradient>
    </defs>
  </svg>
);
export default SpaceScannerPopup;
